import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Box, Button, Checkbox, Container, FormControlLabel,
  Paper, Stack, TextField, Typography,
} from "@mui/material";
import PreviewIcon from "@mui/icons-material/Preview";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import OfferDialog from "components/Adverts/OfferDialog";
import PublicationRulesDialog from "components/Adverts/PublicationRulesDialog";
import OrderSummary from "components/Adverts/OrderSummary";

const MAX_TEXT = 1000;
const FORM_STORAGE_KEY = "advert_form_data";
const SUBSCRIBER_STORAGE_KEY = "checkSubscriber";
const DRAFT_KEY = "advert_draft";

function loadJSON(storage, key) {
  try {
    return JSON.parse(storage.getItem(key) || "null");
  } catch {
    return null;
  }
}

function loadSubscriberName() {
  const data = loadJSON(localStorage, SUBSCRIBER_STORAGE_KEY);
  const surname = typeof data?.surname === "string" ? data.surname.trim() : "";
  const personal = typeof data?.personal === "string" ? data.personal.trim() : "";
  return surname && personal
    ? `${surname.charAt(0).toUpperCase()}${surname.slice(1)} (л/с ${personal})`
    : "";
}

function loadFormData() {
  const data = loadJSON(localStorage, FORM_STORAGE_KEY) || {};
  return {
    name: typeof data.name === "string" ? data.name : "",
    phone: typeof data.phone === "string" ? data.phone : "",
    companyDetails: typeof data.companyDetails === "string" ? data.companyDetails : "",
    showPhone: typeof data.showPhone === "boolean" ? data.showPhone : true,
    cashless: typeof data.cashless === "boolean" ? data.cashless : false,
  };
}

function isValidPhone(value) {
  return /^\d-\d{2}-\d{2}$/.test(value) || /^8-\d{3}-\d{3}-\d{2}-\d{2}$/.test(value);
}

function formatPhone(value) {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 5) {
    return [digits.slice(0, 1), digits.slice(1, 3), digits.slice(3, 5)]
      .filter(Boolean).join("-");
  }
  return [digits.slice(0, 1), digits.slice(1, 4), digits.slice(4, 7),
    digits.slice(7, 9), digits.slice(9, 11)].filter(Boolean).join("-");
}

function pluralDays(value) {
  const mod10 = value % 10;
  const mod100 = value % 100;
  if (mod10 === 1 && mod100 !== 11) return "день";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "дня";
  return "дней";
}

function RangeDay({ day, dateStart, dateEnd, ...other }) {
  const isStart = Boolean(dateStart && day.isSame(dateStart, "day"));
  const isEnd = Boolean(dateEnd && day.isSame(dateEnd, "day"));
  const isInside = Boolean(dateStart && dateEnd &&
    day.isAfter(dateStart, "day") && day.isBefore(dateEnd, "day"));
  const selected = isStart || isEnd;
  return (
    <Box sx={{ position: "relative", display: "flex", justifyContent: "center",
      ...(isInside && { bgcolor: "action.selected" }),
      ...(isStart && dateEnd && { background: (theme) =>
        `linear-gradient(to right, transparent 50%, ${theme.palette.action.selected} 50%)` }),
      ...(isEnd && dateStart && { background: (theme) =>
        `linear-gradient(to right, ${theme.palette.action.selected} 50%, transparent 50%)` }),
    }}>
      <PickersDay {...other} day={day} selected={selected} sx={{ zIndex: 1,
        "&.MuiPickersDay-today": { border: "none" },
        ...(selected && { fontWeight: 600 }),
      }} />
    </Box>
  );
}

export default function AddPage() {
  const navigate = useNavigate();
  const [savedFormData] = useState(loadFormData);
  const [savedSubscriberName] = useState(loadSubscriberName);
  const [draft] = useState(() => loadJSON(sessionStorage, DRAFT_KEY));

  const [cashless, setCashless] = useState(draft?.cashless ?? savedFormData.cashless);
  const [name, setName] = useState(draft?.name ??
    (!savedFormData.cashless && savedSubscriberName ? savedSubscriberName : savedFormData.name));
  const [phone, setPhone] = useState(draft?.phone ?? savedFormData.phone);
  const [showPhone, setShowPhone] = useState(draft?.showPhone ?? savedFormData.showPhone);
  const [companyDetails, setCompanyDetails] = useState(draft?.companyDetails ?? savedFormData.companyDetails);
  const [offerAccepted, setOfferAccepted] = useState(draft?.offerAccepted ?? false);
  const [orderSummary, setOrderSummary] = useState([0, 0, 0, 0]);
  const [dateStart, setDateStart] = useState(() =>
    draft?.dateStart && dayjs(draft.dateStart).isValid()
      ? dayjs(draft.dateStart) : dayjs().add(1, "day"));
  const [dateEnd, setDateEnd] = useState(() =>
    draft?.dateEnd && dayjs(draft.dateEnd).isValid() ? dayjs(draft.dateEnd) : null);
  const [text, setText] = useState(draft?.text ?? "");
  const showTV = true;
  const showInt = true;
  const showPan = true;
  const [error, setError] = useState("");

  useEffect(() => {
    try {
      localStorage.setItem(FORM_STORAGE_KEY,
        JSON.stringify({ name, phone, companyDetails, showPhone, cashless }));
    } catch { /* Storage недоступен */ }
  }, [name, phone, companyDetails, showPhone, cashless]);

  const textLength = Array.from(text).length;
  const phoneError = phone.trim() !== "" && !isValidPhone(phone);
  const periodError = !dateStart || !dateEnd || dateEnd.isBefore(dateStart, "day");
  const placeSelected = showTV || showInt || showPan;
  const periodDays = !periodError ? dateEnd.diff(dateStart, "day") + 1 : 0;
  const isTodayStart = Boolean(dateStart?.isSame(dayjs(), "day"));
  const isAfterTenToday = isTodayStart && dayjs().hour() >= 10;

  const canSend = name.trim() !== "" && (!cashless || companyDetails.trim() !== "") &&
    isValidPhone(phone) && !periodError && text.trim() !== "" &&
    orderSummary[0] >= 2 && placeSelected && offerAccepted;

  const clearMessages = () => setError("");

  const handleTextChange = (event) => {
    setText(Array.from(event.target.value.replace(/[\r\n]+/g, " "))
      .slice(0, MAX_TEXT).join(""));
    clearMessages();
  };

  const handleDateChange = (value) => {
    if (!value) return;
    clearMessages();
    if (!dateStart || dateEnd || value.isBefore(dateStart, "day")) {
      setDateStart(value);
      setDateEnd(null);
    } else {
      setDateEnd(value);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSend) return;

    const readyText = [
      text.trim(),
      showPhone && phone.trim() ? `Тел. ${phone.trim()}` : "",
    ].filter(Boolean).join(" ");

    const draftData = {
      name: name.trim(),
      phone: phone.trim(),
      text: text.trim(),
      companyDetails: companyDetails.trim(),
      showPhone,
      cashless,
      offerAccepted,
      dateStart: dateStart.format("YYYY-MM-DD"),
      dateEnd: dateEnd.format("YYYY-MM-DD"),
      showTV,
      showInt,
      showPan,
      orderSummary: [...orderSummary],
      payload: {
        op: "guestAddStroka",
        name: name.trim(),
        phone: phone.trim(),
        text: readyText,
        datestart: dateStart.startOf("day").unix().toString(),
        dateend: dateEnd.endOf("day").unix().toString(),
        amount: Number(orderSummary[3]).toFixed(2),
        discount: orderSummary[2],
        beznal: cashless ? 1 : 0,
        address: cashless ? companyDetails.trim() : "",
        sh_tv: showTV ? 1 : 0,
        sh_int: showInt ? 1 : 0,
        sh_pan: showPan ? 1 : 0,
      },
    };

    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draftData));
      navigate("/add-preview");
    } catch {
      setError("Не удалось сохранить объявление для предпросмотра");
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
      <Container maxWidth="md" sx={{ py: { xs: 3, sm: 5 }, maxWidth: "800px !important" }}>
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, bgcolor: "background.paper",
          border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 0.5, mb: 3 }}>
              <Typography variant="h4" sx={{ fontSize: { xs: "1.7rem", sm: "2rem" } }}>
                Подать объявление
              </Typography>
              <PublicationRulesDialog />
            </Box>

            <Stack spacing={2}>
              <FormControlLabel
                control={<Checkbox checked={cashless} onChange={(e) => {
                  setCashless(e.target.checked); clearMessages();
                }} />}
                label="Безналичный расчёт для юрлиц"
              />

              <TextField label={cashless ? "Название организации" : "ФИО"} value={name}
                onChange={(e) => { setName(e.target.value); clearMessages(); }}
                fullWidth inputProps={{ maxLength: 250 }} />

              {cashless && (
                <TextField label="Реквизиты организации" value={companyDetails}
                  onChange={(e) => { setCompanyDetails(e.target.value); clearMessages(); }}
                  multiline minRows={4} fullWidth
                  placeholder="УНП, юридический адрес, расчётный счёт, банк, БИК и тп."
                  inputProps={{ maxLength: 1000 }} />
              )}

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr auto" }, gap: 2, alignItems: "start" }}>
                <TextField label="Телефон" value={phone}
                  onChange={(e) => { setPhone(formatPhone(e.target.value)); clearMessages(); }}
                  placeholder="8-029-123-45-67" error={phoneError}
                  helperText={phoneError ? "Введите корректный номер телефона" : " "}
                  fullWidth inputProps={{ maxLength: 50 }} />
                <FormControlLabel
                  control={<Checkbox checked={showPhone} onChange={(e) => {
                    setShowPhone(e.target.checked); clearMessages();
                  }} />}
                  label={<Box sx={{ fontSize: "12px", lineHeight: "15px" }}>
                    Использовать тлф в объявлении
                    <Box sx={{ color: "text.secondary", fontStyle: "italic" }}>(не тарифицируется)</Box>
                  </Box>}
                  sx={{ mt: { xs: 0, sm: 1 }, whiteSpace: "nowrap" }}
                />
              </Box>

              <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                <DateCalendar
                  value={dateEnd || dateStart} onChange={handleDateChange}
                  minDate={dayjs().hour() >= 20 ? dayjs().add(1, "day") : dayjs()}
                  maxDate={dayjs().add(2, "month").endOf("month")}
                  views={["day"]} openTo="day"
                  slots={{ day: RangeDay }} slotProps={{ day: { dateStart, dateEnd } }}
                  sx={{ width: "100%", "& .MuiDayCalendar-weekContainer": { margin: 0 },
                    "& .MuiDayCalendar-weekDayLabel": { width: 40 } }}
                />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, textAlign: "center" }}>
                  Период: <strong>
                    {dateStart ? `с ${dateStart.format("DD.MM.YYYY")} по ` : "—"}
                    {!dateEnd && "..."}
                    {dateEnd && <>
                      {dateEnd.format("DD.MM.YYYY")}
                      <Box component="span" sx={{ ml: 1.5, color: "success.main", fontWeight: 700 }}>
                        {periodDays} {pluralDays(periodDays)}
                      </Box>
                    </>}
                  </strong>
                </Typography>
                {periodError && <Alert severity="warning" sx={{ mx: 2, mb: 1.5 }}>
                  Выберите дату последнего дня трансляции
                </Alert>}
                {isTodayStart && <Alert severity="info" sx={{ mx: 2, mb: 1.5 }}>
                  Объявление будет опубликовано сегодня, через 10-15 минут после оплаты
                </Alert>}
                {isAfterTenToday && <Alert severity="warning" sx={{ mx: 2, mb: 1.5 }}>
                  Обращаем Ваше внимание, что обновление городских светодиодных панелей
                  происходит ежедневно до 10 часов утра
                </Alert>}
              </Paper>

              <Box sx={{ position: "relative" }}>
                <TextField label="Текст объявления (от 2 слов)" value={text}
                  onChange={handleTextChange}
                  onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                  multiline minRows={3} fullWidth helperText={`${textLength} / ${MAX_TEXT}`}
                  sx={{ "& .MuiInputBase-inputMultiline": {
                    pb: showPhone && phone.trim() ? 4.5 : 2,
                  } }}
                  FormHelperTextProps={{ sx: { textAlign: "right", mx: 0 } }}
                />
                {showPhone && phone.trim() !== "" && (
                  <Typography variant="body2" sx={{ position: "absolute", right: 14,
                    bottom: 29, fontWeight: 500, pointerEvents: "none" }}>
                    Тел. {phone}
                  </Typography>
                )}
              </Box>

              <Box sx={{ pt: 1 }}>
                <OrderSummary text={text} periodDays={periodDays}
                  periodLabel={pluralDays(periodDays)} cashless={cashless}
                  onSummaryChange={setOrderSummary} />
                <FormControlLabel
                  control={<Checkbox checked={offerAccepted}
                    onChange={(e) => { setOfferAccepted(e.target.checked); clearMessages(); }} />}
                  label={<Typography component="span">Я ознакомлен и согласен с <OfferDialog /></Typography>}
                  sx={{ m: 1 }}
                />
                {error && <Alert severity="error">{error}</Alert>}
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, width: "100%" }}>
                  <Button type="submit" variant="contained" size="large"
                    disabled={!canSend} startIcon={<PreviewIcon />}>
                    Предпросмотр и оплата
                  </Button>
                </Box>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </LocalizationProvider>
  );
}
